import { useTranslation } from 'react-i18next';
import { LogoLoader } from './LogoLoader';

export interface PageLoaderProps {
  /** Optional caption beneath the logo. Translate at the call site. */
  caption?: string;
  /** Logo width in px. Default 200. */
  size?: number;
  className?: string;
}

/**
 * PageLoader — a non-modal, centered brand loader for an initial route/page
 * load where the content has no meaningful skeleton shape (the website
 * editor, the design wizard). Unlike LoadingOverlay it does NOT dim the
 * screen or trap focus, so it reads as "this page is loading" rather than
 * "a blocking action is in progress".
 *
 * Pick the loader by shape:
 *   - tables / lists / grids      → TableSkeleton
 *   - forms / detail panels       → FormSkeleton
 *   - a whole page with no shape  → PageLoader (this)
 *   - a genuinely blocking op      → LoadingOverlay (modal, focus-trapped)
 */
export function PageLoader({ caption, size = 200, className }: PageLoaderProps) {
  const { t } = useTranslation('common');
  const label = caption ?? t('common.loading');
  return (
    <div
      className={`flex min-h-[60vh] w-full flex-col items-center justify-center gap-3${
        className ? ` ${className}` : ''
      }`}
    >
      <LogoLoader label={label} size={size} />
      {caption && <p className="text-sm text-muted-foreground">{caption}</p>}
    </div>
  );
}
